import type { GuestAcquisitionCtaMode } from "../config/guestAcquisition";
import {
  productAnalytics,
  productEvents,
  type ProductAnalyticsRecording,
} from "../productAnalytics";
import { createBrowserCtaViewability } from "./browserCtaViewability";

export const GUEST_ACQUISITION_AVAILABILITY_THRESHOLD_MS = 5_000;
export const GUEST_ACQUISITION_VIEWABLE_DWELL_MS = 1_000;

type AccessState = Parameters<
  typeof productEvents.guestSharedListView
>[0]["access_state"];
type AvailabilityMode = Parameters<
  typeof productEvents.guestSharedListAvailability
>[0]["mode"];
type ConnectionState = Parameters<
  typeof productEvents.guestSharedListAvailability
>[0]["connection_state"];

export type GuestAcquisitionAction = "add_item" | "toggle_item" | "rename_item";

export type GuestAcquisitionSurfaceObservation =
  | {
      kind: "loading";
      connectionState: ConnectionState;
    }
  | {
      kind: "available";
      access: {
        editingAllowed: boolean;
        expiresAt: number | null;
        permanent: boolean;
      };
      connectionState: ConnectionState;
      dataSource: "live" | "saved";
    }
  | {
      kind: "terminal_error";
      connectionState: ConnectionState;
      failure: "access_unavailable" | "load_error";
    }
  | {
      kind: "not_found";
      connectionState: ConnectionState;
    };

export interface GuestAcquisitionActionOutcome {
  action: GuestAcquisitionAction;
  result: "success" | "failure" | "blocked";
  reason?: "editing_disabled";
}

export interface GuestAcquisitionClock {
  now(): number;
}

export interface GuestAcquisitionScheduler {
  schedule(delayMs: number, action: () => void): () => void;
}

export interface GuestAcquisitionViewability {
  observe(
    onViewabilityChanged: (isAtLeastHalfVisible: boolean) => void,
  ): () => void;
}

export interface GuestAcquisitionObservation {
  beginRoute(routeKey: unknown): void;
  endRoute(): void;
  observeSurface(observation: GuestAcquisitionSurfaceObservation): void;
  observeActionOutcome(outcome: GuestAcquisitionActionOutcome): boolean;
  observeCtaEligibility(isRendered: boolean): void;
  observeCtaClick(): void;
  dispose(): void;
}

export interface GuestAcquisitionObservationDependencies {
  recorder: ProductAnalyticsRecording;
  clock: GuestAcquisitionClock;
  scheduler: GuestAcquisitionScheduler;
  viewability?: GuestAcquisitionViewability;
}

interface AvailabilityCandidate {
  connectionState: ConnectionState;
  mode: AvailabilityMode;
}

const isKnownAction = (value: unknown): value is GuestAcquisitionAction =>
  value === "add_item" || value === "toggle_item" || value === "rename_item";

const isKnownResult = (
  value: unknown,
): value is GuestAcquisitionActionOutcome["result"] =>
  value === "success" || value === "failure" || value === "blocked";

class DefaultGuestAcquisitionObservation implements GuestAcquisitionObservation {
  private routeKey: unknown;
  private routeGeneration = 0;
  private routeStarted = false;
  private routeActive = false;
  private disposed = false;
  private entryOutcomeRecorded = false;
  private accessState: AccessState = "access_unavailable";
  private ctaEligible = false;
  private ctaImpressionRecorded = false;
  private availabilityCandidate: AvailabilityCandidate | null = null;
  private activeAvailabilityMode: AvailabilityMode | null = null;
  private cancelAvailabilityTimer: (() => void) | null = null;
  private cancelViewability: (() => void) | null = null;
  private cancelViewableDwell: (() => void) | null = null;

  constructor(
    private readonly ctaMode: GuestAcquisitionCtaMode,
    private readonly dependencies: GuestAcquisitionObservationDependencies,
  ) {}

  beginRoute(routeKey: unknown): void {
    try {
      if (this.disposed) return;
      if (this.routeStarted && Object.is(this.routeKey, routeKey)) {
        this.routeActive = true;
        return;
      }

      this.cancelPendingAvailability();
      this.stopViewability();
      this.routeKey = routeKey;
      this.routeGeneration += 1;
      this.routeStarted = true;
      this.routeActive = true;
      this.entryOutcomeRecorded = false;
      this.accessState = "access_unavailable";
      this.ctaEligible = false;
      this.ctaImpressionRecorded = false;
      this.availabilityCandidate = null;
      this.activeAvailabilityMode = null;
    } catch {
      // Observation state must never affect Shared Grocery List behavior.
    }
  }

  endRoute(): void {
    try {
      if (this.disposed || !this.routeActive) return;
      this.routeGeneration += 1;
      this.cancelPendingAvailability();
      this.stopViewability();
      this.routeActive = false;
      this.availabilityCandidate = null;
    } catch {
      // Route teardown is best effort and never part of the shopping task.
    }
  }

  observeSurface(observation: GuestAcquisitionSurfaceObservation): void {
    try {
      if (this.disposed || !this.routeActive) return;

      switch (observation.kind) {
        case "loading":
          this.observeAvailability({
            mode: "prolonged_loading",
            connectionState: observation.connectionState,
          });
          return;
        case "terminal_error":
          this.cancelPendingAvailability();
          this.recordTerminalEntry(observation.failure);
          return;
        case "not_found":
          this.cancelPendingAvailability();
          this.recordTerminalEntry("not_found");
          return;
        case "available":
          this.accessState = this.classifyAccess(observation.access);
          if (observation.dataSource === "saved") {
            this.observeAvailability({
              mode: "saved_snapshot",
              connectionState: observation.connectionState,
            });
          } else {
            this.observeAuthoritativeRecovery(observation.connectionState);
          }
          this.recordValidEntry();
      }
    } catch {
      // Observation state must never affect Shared Grocery List behavior.
    }
  }

  observeActionOutcome(outcome: GuestAcquisitionActionOutcome): boolean {
    try {
      if (
        this.disposed ||
        !this.routeActive ||
        !isKnownAction(outcome.action) ||
        !isKnownResult(outcome.result)
      ) {
        return this.ctaEligible;
      }

      const reason =
        outcome.result === "blocked" && outcome.reason === "editing_disabled"
          ? outcome.reason
          : undefined;
      this.safeRecord(() =>
        productEvents.guestSharedListAction({
          surface: "shared_list",
          action: outcome.action,
          access_state: this.accessState,
          cta_mode: this.ctaMode,
          result: outcome.result,
          ...(reason ? { reason } : {}),
        }),
      );
      if (outcome.result === "success") this.ctaEligible = true;
    } catch {
      // The eligibility decision is independent of analytics delivery.
      if (outcome?.result === "success") this.ctaEligible = true;
    }
    return this.ctaEligible;
  }

  observeCtaEligibility(isRendered: boolean): void {
    try {
      if (
        this.disposed ||
        !this.routeActive ||
        !isRendered ||
        !this.ctaEligible ||
        this.ctaImpressionRecorded
      ) {
        if (!isRendered) this.stopViewability();
        return;
      }
      if (this.cancelViewability || !this.dependencies.viewability) return;

      const cancel = this.dependencies.viewability.observe((isViewable) => {
        try {
          this.observeViewabilityChange(isViewable);
        } catch {
          // Observation callbacks must never escape into browser behavior.
        }
      });
      this.cancelViewability = typeof cancel === "function" ? cancel : () => {};
    } catch {
      // Missing or throwing viewability support simply omits the impression.
    }
  }

  observeCtaClick(): void {
    try {
      if (this.disposed || !this.routeActive || !this.ctaEligible) return;
      this.safeRecord(() =>
        productEvents.guestSharedListCtaClick({
          surface: "shared_list",
          access_state: this.accessState,
          cta_mode: this.ctaMode,
        }),
      );
    } catch {
      // CTA navigation must never depend on recording.
    }
  }

  dispose(): void {
    try {
      if (this.disposed) return;
      this.endRoute();
      this.disposed = true;
    } catch {
      // Disposal is best effort and never part of the shopping task.
    }
  }

  private classifyAccess(access: {
    editingAllowed: boolean;
    expiresAt: number | null;
    permanent: boolean;
  }): AccessState {
    if (
      access.permanent !== true &&
      typeof access.expiresAt === "number" &&
      Number.isFinite(access.expiresAt) &&
      access.expiresAt <= this.dependencies.clock.now()
    ) {
      return "expired";
    }
    return access.editingAllowed ? "editable" : "read_only";
  }

  private recordValidEntry(): void {
    if (this.entryOutcomeRecorded) return;
    this.entryOutcomeRecorded = true;
    this.safeRecord(() =>
      productEvents.guestSharedListView({
        surface: "shared_list",
        access_state: this.accessState,
        cta_mode: this.ctaMode,
      }),
    );
  }

  private recordTerminalEntry(
    failure: "access_unavailable" | "load_error" | "not_found",
  ): void {
    if (this.entryOutcomeRecorded) return;
    this.entryOutcomeRecorded = true;
    this.accessState = failure;
    this.safeRecord(() =>
      productEvents.guestSharedListAccessError({
        surface: "shared_list",
        access_state: failure,
        cta_mode: this.ctaMode,
        result: "blocked",
      }),
    );
  }

  private observeAvailability(candidate: AvailabilityCandidate): void {
    if (this.activeAvailabilityMode) return;
    if (
      this.availabilityCandidate?.mode === candidate.mode &&
      this.cancelAvailabilityTimer
    ) {
      // Keep the original five-second dwell for a continuous availability
      // mode, but classify detection using the latest connection observation.
      this.availabilityCandidate = candidate;
      return;
    }

    this.cancelPendingAvailability();
    this.availabilityCandidate = candidate;
    const generation = this.routeGeneration;
    try {
      const cancel = this.dependencies.scheduler.schedule(
        GUEST_ACQUISITION_AVAILABILITY_THRESHOLD_MS,
        () => {
          try {
            const currentCandidate = this.availabilityCandidate;
            if (
              this.disposed ||
              generation !== this.routeGeneration ||
              this.activeAvailabilityMode ||
              currentCandidate?.mode !== candidate.mode
            ) {
              return;
            }
            this.safeRecord(() =>
              productEvents.guestSharedListAvailability({
                surface: "shared_list",
                phase: "detected",
                mode: candidate.mode,
                connection_state: currentCandidate.connectionState,
              }),
            );
            this.activeAvailabilityMode = candidate.mode;
            this.availabilityCandidate = null;
            this.cancelAvailabilityTimer = null;
          } catch {
            // A stale or failing timer cannot escape into the feature.
          }
        },
      );
      this.cancelAvailabilityTimer =
        typeof cancel === "function" ? cancel : () => {};
    } catch {
      this.availabilityCandidate = null;
      this.cancelAvailabilityTimer = null;
    }
  }

  private observeAuthoritativeRecovery(connectionState: ConnectionState): void {
    this.cancelPendingAvailability();
    const recoveredMode = this.activeAvailabilityMode;
    if (!recoveredMode) return;

    this.activeAvailabilityMode = null;
    this.safeRecord(() =>
      productEvents.guestSharedListAvailability({
        surface: "shared_list",
        phase: "recovered",
        mode: recoveredMode,
        connection_state: connectionState,
      }),
    );
  }

  private cancelPendingAvailability(): void {
    const cancel = this.cancelAvailabilityTimer;
    this.cancelAvailabilityTimer = null;
    this.availabilityCandidate = null;
    if (!cancel) return;
    try {
      cancel();
    } catch {
      // Scheduling is optional observation infrastructure.
    }
  }

  private observeViewabilityChange(isAtLeastHalfVisible: boolean): void {
    if (!isAtLeastHalfVisible) {
      this.cancelViewableDwellTimer();
      return;
    }
    if (this.ctaImpressionRecorded || this.cancelViewableDwell) return;

    const generation = this.routeGeneration;
    try {
      const cancel = this.dependencies.scheduler.schedule(
        GUEST_ACQUISITION_VIEWABLE_DWELL_MS,
        () => {
          try {
            if (
              this.disposed ||
              generation !== this.routeGeneration ||
              this.ctaImpressionRecorded ||
              !this.ctaEligible
            ) {
              return;
            }
            this.safeRecord(() =>
              productEvents.guestSharedListCtaImpression({
                surface: "shared_list",
                access_state: this.accessState,
                cta_mode: this.ctaMode,
              }),
            );
            this.ctaImpressionRecorded = true;
            this.cancelViewableDwell = null;
            this.stopViewability();
          } catch {
            // A stale or failing timer cannot escape into the feature.
          }
        },
      );
      this.cancelViewableDwell =
        typeof cancel === "function" ? cancel : () => {};
    } catch {
      this.cancelViewableDwell = null;
    }
  }

  private cancelViewableDwellTimer(): void {
    const cancel = this.cancelViewableDwell;
    this.cancelViewableDwell = null;
    if (!cancel) return;
    try {
      cancel();
    } catch {
      // Scheduling is optional observation infrastructure.
    }
  }

  private stopViewability(): void {
    this.cancelViewableDwellTimer();
    const cancel = this.cancelViewability;
    this.cancelViewability = null;
    if (!cancel) return;
    try {
      cancel();
    } catch {
      // Viewability is optional observation infrastructure.
    }
  }

  private safeRecord(
    createEvent: () => Parameters<ProductAnalyticsRecording["record"]>[0],
  ): void {
    try {
      this.dependencies.recorder.record(createEvent());
    } catch {
      // Product Analytics is never a dependency of product behavior.
    }
  }
}

export const createGuestAcquisitionObservation = (
  ctaMode: GuestAcquisitionCtaMode,
  dependencies: GuestAcquisitionObservationDependencies,
): GuestAcquisitionObservation =>
  new DefaultGuestAcquisitionObservation(ctaMode, dependencies);

export const createBrowserGuestAcquisitionObservation = (
  ctaMode: GuestAcquisitionCtaMode,
): GuestAcquisitionObservation =>
  createGuestAcquisitionObservation(ctaMode, {
    recorder: productAnalytics,
    clock: { now: () => Date.now() },
    scheduler: {
      schedule: (delayMs, action) => {
        const timer = window.setTimeout(action, delayMs);
        return () => window.clearTimeout(timer);
      },
    },
    viewability: createBrowserCtaViewability(),
  });
